import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { FaArrowLeft, FaCheckCircle, FaTruck, FaTimes, FaShoppingBag, FaTag } from 'react-icons/fa';
import AppContext from '../../../context/AppContext';
import '../../../assests/css/customer/NotificationStyles.css';

const formatTime = (raw) => {
  if (!raw) return '';
  const date = new Date(raw);
  if (isNaN(date)) return raw;
  const diff = Math.floor((Date.now() - date) / 1000);
  if (diff < 60)     return 'Just now';
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

const getConfig = (title) => {
  const t = title?.toLowerCase() || '';
  if (t.includes('sale') || t.includes('live'))
    return { icon: <FaTag />,         className: 's-shipped',    label: 'Sale'         };
  if (t.includes('placed') || t.includes('congratulations'))
    return { icon: <FaShoppingBag />, className: 's-delivered',  label: 'Order Placed' };
  if (t.includes('shipped'))
    return { icon: <FaTruck />,       className: 's-shipped',    label: 'Shipped'      };
  if (t.includes('delivered'))
    return { icon: <FaCheckCircle />, className: 's-delivered',  label: 'Delivered'    };
  if (t.includes('cancelled') || t.includes('failed'))
    return { icon: <FaTimes />,       className: 's-cancelled',  label: 'Cancelled'    };
  return   { icon: <FaShoppingBag />, className: 's-processing', label: 'Notification' };
};

const stopBubble = (e) => e.stopPropagation();

const NotificationModal = ({ notification, onClose, onDelete }) => {
  const config = getConfig(notification.title);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={stopBubble}>
        <button className="modal-close" onClick={onClose}>×</button>
        <div className={`cn-icon-wrap modal-icon-wrap ${config.className}`}>
          {config.icon}
        </div>
        <h2 className="modal-title">{notification.title}</h2>
        <p className="modal-time">{notification.timestamp}</p>
        <div className="modal-body">
          <p>{notification.details || notification.description}</p>
        </div>
        <div className="modal-delete-wrapper">
          <button className="modal-delete-btn" onClick={() => onDelete(notification.id)}>
            🗑 Delete Notification
          </button>
        </div>
      </div>
    </div>
  );
};

const NotificationStyles = () => {
  const { baseUrl, customer }  = useContext(AppContext);
  const navigate     = useNavigate();
  const location     = useLocation();

  const [activeTab,            setActiveTab]            = useState('all');
  const [searchQuery,          setSearchQuery]          = useState('');
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notifications,        setNotifications]        = useState([]);
  const [loading,              setLoading]              = useState(true);
  const [highlightedId,        setHighlightedId]        = useState(null);

  const customerId = customer?.id;

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const readCount   = notifications.filter(n =>  n.isRead).length;

  const filtered = notifications
    .filter(n => activeTab === 'read' ? n.isRead : activeTab === 'unread' ? !n.isRead : true)
    .filter(n => !searchQuery.trim() ||
      n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      n.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

  // ── FETCH ──────────────────────────────────────────────────
  const fetchAll = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/customer/notification/getnotifications`, {
        params: { customer_id: customerId }
      })
      .then((res) => {
        const mapped = (res.data?.notifications || []).map(n => ({
          id:          n.id,
          title:       n.title       || 'Notification',
          description: n.description || '',
          details:     n.detail      || n.description || '',
          timestamp:   formatTime(n.created_at),
          rawDate:     n.created_at,
          isRead:      n.status !== 'unread',
          status:      n.status,
        }));
        setNotifications(mapped);

        const openId = location.state?.openId;
        if (openId) {
          const target = mapped.find(n => n.id === openId);
          if (target) {
            setSelectedNotification(target);
            setHighlightedId(openId);
            window.history.replaceState({}, '');
          }
        }
      })
      .catch(() => Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load notifications.', confirmButtonColor: '#8B7355' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!customerId) return;
    fetchAll();
  }, [customerId, baseUrl]);
  // ── MARK READ ─────────────────────────────────────────────
  const markRead = (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    axios.post(`${baseUrl}/customer/notification/markasreadnotification`, { id }).catch(() => {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: false } : n));
    });
  };

  const markAllRead = () => {
    const unreadIds = notifications.filter(n => !n.isRead).map(n => n.id);
    if (!unreadIds.length) return;
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    Promise.all(
      unreadIds.map(id => axios.post(`${baseUrl}/customer/notification/markasreadnotification`, { id }))
    ).catch(() => {
      setNotifications(prev => prev.map(n => unreadIds.includes(n.id) ? { ...n, isRead: false } : n));
    });
  };

  // ── DELETE ────────────────────────────────────────────────
  const removeNotification = (id) => {
    Swal.fire({
      title: 'Delete Notification?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e53935',
      cancelButtonColor: '#8B7355',
      confirmButtonText: 'Yes, delete',
    }).then((result) => {
      if (!result.isConfirmed) return;
      axios.post(`${baseUrl}/customer/notification/deletenotifications`, { id })
        .then((res) => {
          if (res.data?.success) {
            setNotifications(prev => prev.filter(n => n.id !== id));
            if (selectedNotification?.id === id) setSelectedNotification(null);
            if (highlightedId === id) setHighlightedId(null);
            Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
          }
        })
        .catch(() => Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete.', confirmButtonColor: '#8B7355' }));
    });
  };

  // ── OPEN MODAL ────────────────────────────────────────────
  const openNotification = (n) => {
    if (!n.isRead) markRead(n.id);
    setSelectedNotification(n);
    setHighlightedId(n.id);
  };

  // ── RENDER ─────────────────────────────────────────────────
  return (
    <div className="notification-page">

      {/* Header */}
      <div className="cn-header">
        <button className="cn-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft size={18} />
        </button>
        <h1 className="cn-title">Notifications</h1>
        <div className="cn-header-right">
          {unreadCount > 0 && <span className="cn-badge">{unreadCount}</span>}
          {unreadCount > 0 && (
            <button className="cn-mark-all" onClick={markAllRead}>Mark all read</button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="notification-search-wrapper">
        <div className="notification-search-box">
          <svg width="16" height="16" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && <button className="search-clear-btn" onClick={() => setSearchQuery('')}>×</button>}
        </div>
      </div>

      {/* Tabs */}
      <div className="notification-controls">
        <div className="tabs-container">
          <button className={`tab ${activeTab === 'all'    ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
            All <span className="tab-count">{notifications.length}</span>
          </button>
          <button className={`tab ${activeTab === 'unread' ? 'active' : ''}`} onClick={() => setActiveTab('unread')}>
            Unread <span className="tab-count">{unreadCount}</span>
          </button>
          <button className={`tab ${activeTab === 'read'   ? 'active' : ''}`} onClick={() => setActiveTab('read')}>
            Read <span className="tab-count">{readCount}</span>
          </button>
        </div>
      </div>

      {/* List */}
      <div className="cn-list">
        {loading ? (
          <p className="text-center mt-4">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No Notifications</h3>
            <p>{searchQuery ? 'No results match your search.' : "You're all caught up!"}</p>
          </div>
        ) : (
          filtered.map((n) => {
            const config = getConfig(n.title);
            return (
              <div
                key={n.id}
                className={`cn-row ${config.className} ${!n.isRead ? 'unread' : ''} ${highlightedId === n.id ? 'highlighted' : ''}`}
                onClick={() => openNotification(n)}
                style={{ cursor: 'pointer' }}
              >
                <div className="cn-icon-wrap">{config.icon}</div>
                <div className="cn-content">
                  <h4>{n.title}</h4>
                  <p>{n.description}</p>
                  <div className="cn-meta">
                    <span className="cn-pill">{config.label}</span>
                    <span className="cn-time">{n.rawDate ? new Date(n.rawDate).toLocaleString() : n.timestamp}</span>
                  </div>
                </div>
                <div className="cn-actions" onClick={stopBubble}>
                  {!n.isRead && <div className="unread-indicator" />}
                  <button className="notif-delete-btn" onClick={() => removeNotification(n.id)} title="Delete">🗑</button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {selectedNotification && (
        <NotificationModal
          notification={selectedNotification}
          onClose={() => { setSelectedNotification(null); setHighlightedId(null); }}
          onDelete={removeNotification}
        />
      )}
    </div>
  );
};

export default NotificationStyles;